import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AdminStats, ApiResponse, PaginatedResponse } from '@/types';
import api from '@/services/authService';

interface AdminState {
  users: any[];
  transactions: any[];
  stats: AdminStats | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const initialState: AdminState = {
  users: [],
  transactions: [],
  stats: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
};

// Async thunks
export const getUsers = createAsyncThunk(
  'admin/getUsers',
  async (params: { page?: number; limit?: number; search?: string; status?: string; role?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/users', { params });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch users');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch users');
    }
  }
);

export const approveUser = createAsyncThunk(
  'admin/approveUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}/approve`);
      if (response.data.success) {
        return { userId, approved: true };
      }
      throw new Error(response.data.error?.message || 'Failed to approve user');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to approve user');
    }
  }
);

export const rejectUser = createAsyncThunk(
  'admin/rejectUser',
  async ({ userId, reason }: { userId: number; reason: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}/reject`, { reason });
      if (response.data.success) {
        return { userId, rejected: true };
      }
      throw new Error(response.data.error?.message || 'Failed to reject user');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to reject user');
    }
  }
);

export const adjustUserBalance = createAsyncThunk(
  'admin/adjustUserBalance',
  async ({ userId, amount, currency, adjustmentType, reason }: any, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/admin/users/${userId}/balance-adjustment`, {
        amount,
        currency,
        adjustmentType,
        reason,
      });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to adjust balance');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to adjust balance');
    }
  }
);

export const getTransactions = createAsyncThunk(
  'admin/getTransactions',
  async (params: { page?: number; limit?: number; status?: string; type?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/transactions', { params });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch transactions');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch transactions');
    }
  }
);

export const approveTransaction = createAsyncThunk(
  'admin/approveTransaction',
  async ({ transactionId, status, comments }: any, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/admin/transactions/${transactionId}/approve`, {
        status,
        comments,
      });
      if (response.data.success) {
        return { transactionId, status };
      }
      throw new Error(response.data.error?.message || 'Failed to approve transaction');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to approve transaction');
    }
  }
);

export const getStats = createAsyncThunk(
  'admin/getStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/stats');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error?.message || 'Failed to fetch stats');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch stats');
    }
  }
);

// Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAdminState: (state) => {
      state.users = [];
      state.transactions = [];
      state.stats = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get Users
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Approve User
    builder
      .addCase(approveUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(approveUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const userIndex = state.users.findIndex(user => user.id === action.payload.userId);
        if (userIndex !== -1) {
          state.users[userIndex].registrationStatus = 'approved';
        }
        state.error = null;
      })
      .addCase(approveUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Reject User
    builder
      .addCase(rejectUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const userIndex = state.users.findIndex(user => user.id === action.payload.userId);
        if (userIndex !== -1) {
          state.users[userIndex].registrationStatus = 'rejected';
        }
        state.error = null;
      })
      .addCase(rejectUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Adjust Balance
    builder
      .addCase(adjustUserBalance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adjustUserBalance.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(adjustUserBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Transactions
    builder
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload.transactions;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Approve Transaction
    builder
      .addCase(approveTransaction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(approveTransaction.fulfilled, (state, action) => {
        state.isLoading = false;
        const transactionIndex = state.transactions.findIndex(t => t.id === action.payload.transactionId);
        if (transactionIndex !== -1) {
          state.transactions[transactionIndex].status = action.payload.status;
        }
        state.error = null;
      })
      .addCase(approveTransaction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Get Stats
    builder
      .addCase(getStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(getStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearAdminState } = adminSlice.actions;
export default adminSlice.reducer;